import { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ImageCarousel = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const images = [
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/1i6kaun0_Meta%201.png',
      alt: 'ParachPay Gold Card'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/9g62oepx_Meta%20back%20side.png',
      alt: 'ParachPay Card Back'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/q2jdg3rh_parachpay_card_blue.png',
      alt: 'ParachPay Blue Card'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/volbdvky_parachpay_card_blue_back%20side.png',
      alt: 'ParachPay Blue Card Back'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/922b10po_Social%20media%20post-9.png',
      alt: 'ParachPay Mobile Payment'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/t8abf686_Exchage%20screenshot-1.png',
      alt: 'Currency Exchange Board'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4">
            {t('home.carousel.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('home.carousel.subtitle')}
          </p>
        </div>
        
        {/* Two Column Layout: Features Image + Carousel */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          
          {/* Left Side - Features Image */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-3xl blur-2xl group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all duration-300" />
              <img
                src="https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/ywy10n83_Parachpay%20card-1.png"
                alt="ParachPay Features - Virtual Card, 2FA Security, Global Coverage, 64+ Currencies"
                className="relative w-full max-w-[400px] h-auto rounded-2xl group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
          
          {/* Right Side - Rotating Carousel */}
          <div className="flex-1 flex justify-center lg:justify-start">
            <a 
              href="https://parachpay.com/register" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block relative h-[350px] md:h-[450px] w-full max-w-[450px] flex items-center justify-center cursor-pointer group"
              data-testid="card-carousel-link"
            >
              {/* Glow effect */}
              <div className="absolute w-[250px] h-[250px] md:w-[350px] md:h-[350px] bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl group-hover:from-emerald-500/30 group-hover:to-teal-500/30 transition-all duration-300" />
              
              {/* Stacked Images */}
              <div className="relative w-[280px] h-[320px] md:w-[380px] md:h-[420px] group-hover:scale-105 transition-transform duration-300">
                {images.map((image, index) => {
                  const relativeIndex = (index - currentIndex + images.length) % images.length;
                  const isVisible = relativeIndex <= 2;
                  const translateY = relativeIndex * 15;
                  const translateX = relativeIndex * 10;
                  const scale = 1 - (relativeIndex * 0.05);
                  const opacity = relativeIndex === 0 ? 1 : 0.7 - (relativeIndex * 0.2);
                  const zIndex = images.length - relativeIndex;
                  const rotate = relativeIndex * 2;
                  
                  return (
                    <div
                      key={`slide-${image.alt}-${index}`}
                      className={`absolute inset-0 transition-all duration-700 ease-out ${
                        isVisible ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{
                        transform: `
                          translateY(${translateY}px) 
                          translateX(${translateX}px) 
                          scale(${scale})
                          rotate(${rotate}deg)
                        `,
                        opacity: isVisible ? opacity : 0,
                        zIndex,
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-contain drop-shadow-2xl"
                        loading="lazy"
                      />
                    </div>
                  );
                })}
              </div>
              
              {/* Hover overlay with CTA */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                <div className="bg-emerald-600 text-white px-6 py-3 rounded-full flex items-center gap-2 shadow-xl">
                  <span className="font-semibold">{t('home.carousel.getStarted')}</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </a>
          </div>
        </div>
        
        {/* Navigation Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {images.map((image, index) => (
            <button
              key={`dot-${image.alt}`}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-emerald-500 w-8' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        {/* Slide counter */}
        <div className="text-center mt-4">
          <span className="text-sm text-muted-foreground">
            {currentIndex + 1} / {images.length}
          </span>
        </div>
      </div>
    </section>
  );
};

export default ImageCarousel;
